import { Box, BoxProps, Flex, FlexProps } from '@chakra-ui/react'
import {
  OnDatesChangeProps,
  START_DATE,
  useDatepicker,
} from '@datepicker-react/hooks'
import React, { useEffect, useState } from 'react'

import { IconChevronLeft } from '~shared-components/icons/brand/IconChevronLeft'
import { IconChevronRight } from '~shared-components/icons/brand/IconChevronRight'

import { DatePickerContext } from './DatePickerContext'
import { DPMonth } from './DPMonth'
import { DPNav } from './DPNav'

export interface DatePickerProps {
  enableRangeSelection?: boolean
  endDate?: Date
  fullWidth?: boolean
  isDateBlocked?: (date: Date) => boolean
  minWidth?: string
  numberOfMonths?: number
  onDateChange?: (date: OnDatesChangeProps) => void
  startDate?: Date
}

interface DatePickerContainerProps extends BoxProps {
  fullWidth: boolean
}

const DatePickerContainer: React.FC<DatePickerContainerProps> = ({
  fullWidth,
  ...props
}) => (
  <Box
    display={fullWidth ? 'block' : 'inline-block'}
    position="relative"
    {...props}
  />
)

const sxDateGrid = {
  '& > *': {
    flex: '1 0',
    minWidth: 'fit-content',
  },
}

const DateGrid: React.FC<FlexProps> = props => (
  <Flex sx={sxDateGrid} {...props} />
)

const NavContainer: React.FC<FlexProps> = props => (
  <Flex
    justifyContent="space-between"
    position="absolute"
    width="100%"
    {...props}
  />
)

export const DatePicker: React.FC<DatePickerProps> = ({
  enableRangeSelection = false,
  endDate,
  fullWidth = false,
  isDateBlocked: isDateBlockedHandler,
  minWidth,
  numberOfMonths = 1,
  onDateChange,
  startDate,
}) => {
  const [state, setState] = useState<OnDatesChangeProps>({
    endDate: null,
    focusedInput: START_DATE,
    startDate: null,
  })

  const handleDateChange = (data: OnDatesChangeProps) => {
    if (enableRangeSelection) {
      if (!data.focusedInput) {
        setState({ ...data, focusedInput: START_DATE })
      } else {
        setState(data)
      }
    } else {
      setState({
        endDate: data.startDate,
        focusedInput: START_DATE,
        startDate: data.startDate,
      })
    }

    typeof onDateChange === 'function' && onDateChange(data)
  }

  const {
    activeMonths,
    firstDayOfWeek,
    focusedDate,
    goToNextMonths,
    goToPreviousMonths,
    isDateBlocked,
    isDateFocused,
    isDateHovered,
    isDateSelected,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateHover,
    onDateSelect,
  } = useDatepicker({
    endDate: state.endDate,
    focusedInput: state.focusedInput,
    initialVisibleMonth: startDate,
    isDateBlocked: isDateBlockedHandler,
    numberOfMonths,
    onDatesChange: handleDateChange,
    startDate: state.startDate,
  })

  useEffect(() => {
    setState({
      endDate: endDate ? endDate : null,
      focusedInput: state.focusedInput,
      startDate: startDate ? startDate : null,
    })
  }, [endDate, startDate, state.focusedInput])

  return (
    <DatePickerContext.Provider
      value={{
        focusedDate,
        isDateBlocked,
        isDateFocused,
        isDateHovered,
        isDateSelected,
        isFirstOrLastSelectedDate,
        onDateFocus,
        onDateHover,
        onDateSelect,
      }}
    >
      <DatePickerContainer
        data-testid="date-picker"
        fullWidth={fullWidth}
        minWidth={minWidth}
      >
        <NavContainer>
          <DPNav onClick={goToPreviousMonths}>
            <IconChevronLeft boxSize="20px" color="black.0" />
          </DPNav>
          <DPNav onClick={goToNextMonths}>
            <IconChevronRight boxSize="20px" color="black.0" />
          </DPNav>
        </NavContainer>
        <DateGrid>
          {activeMonths.map(month => (
            <DPMonth
              key={`${month.year}-${month.month}`}
              firstDayOfWeek={firstDayOfWeek}
              month={month.month}
              year={month.year}
            />
          ))}
        </DateGrid>
      </DatePickerContainer>
    </DatePickerContext.Provider>
  )
}
